import { Link, Navigate, useParams } from 'react-router-dom';
import { AccessTime, CalendarToday, Event, Videocam } from '@mui/icons-material';
import { Badge, Button, Card, CardContent, CardHeader, Chip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { CardTitle } from '../components/card-title';
import { DetailRow, DateValue } from '../components/detail-row';
import { LoadingState } from '../components/loading-state';
import { ExplorableId } from '../components/explorable-id';
import { IdChip } from '../components/org-page-redesign/primitives';
import { useGetAppointmentDetailsQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

const MODALITY_LABELS: Record<string, string> = {
  in_person: 'In person',
  video: 'Video',
  audio: 'Audio',
};

const getStatusBadgeColor = (
  status: string | undefined | null,
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  if (!status) return 'default';

  switch (status.toLowerCase()) {
    case 'completed':
    case 'success':
    case 'active':
      return 'success';
    case 'scheduled':
      return 'info';
    case 'pending':
    case 'processing':
      return 'warning';
    case 'failed':
    case 'error':
    case 'cancelled':
      return 'error';
    case 'draft':
    case 'inactive':
      return 'secondary';
    default:
      return 'default';
  }
};

export default function AppointmentDetailPage() {
  const {
    providerId: urlProviderId,
    appointmentId: appointmentIdParam,
    id: legacyId,
  } = useParams<{ providerId?: string; appointmentId?: string; id?: string }>();
  const appointmentId = appointmentIdParam || legacyId || '';

  // Always call the appointment details query
  const { data, isLoading } = useGetAppointmentDetailsQuery(
    { appointmentId },
    {
      skip: !appointmentId,
    },
  );

  // Prefer the provider from the URL; fall back to the appointment response
  const providerId = urlProviderId
    ? decodeURIComponent(urlProviderId)
    : data?.appointment?.provider_id;

  // If we don't have the required appointmentId, show error
  if (!appointmentId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Appointment not found</h2>
            <p className="text-gray-600 mt-2">Invalid appointment ID.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading appointment details..." />;
  }

  if (!data?.appointment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Appointment not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Legacy /admin-v2/appointment/:id URL — redirect to the provider-scoped URL
  if (!urlProviderId && providerId) {
    return (
      <Navigate
        to={`/admin-v2/provider/${encodeURIComponent(providerId)}/appointments/${appointmentId}`}
        replace
      />
    );
  }

  const { appointment, encounter } = data;
  const telehealthInfo = appointment.telehealth_info;

  const telehealthChip =
    telehealthInfo?.telehealth_kind === 'jotmeet'
      ? { label: 'Telehealth · JotMeet', color: 'primary' as const }
      : telehealthInfo?.telehealth_kind === 'external_telehealth'
        ? { label: 'Telehealth · External', color: 'info' as const }
        : { label: 'In-person', color: 'default' as const };

  const endTime =
    appointment.start_time && appointment.duration
      ? new Date(new Date(appointment.start_time).getTime() + appointment.duration * 60_000)
      : null;

  const encounterUrl = encounter
    ? providerId
      ? `/admin-v2/provider/${encodeURIComponent(providerId)}/encounters/${encounter.encounter_id}`
      : `/admin-v2/encounter/${encounter.encounter_id}`
    : '';

  return (
    <>
      {/* Appointment header strip; provider identity lives in the passport above */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <CalendarToday className="mr-2 h-6 w-6" />
              {appointment.title || 'Appointment'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {formatDateTime(appointment.start_time)}
              {appointment.duration ? ` · ${appointment.duration} min` : ''}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <ExplorableId
                label="appt"
                value={appointment.appointment_id}
                resourceType="appointment"
                organizationId={appointment.organization_id}
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Chip
              icon={
                telehealthInfo?.telehealth_kind &&
                telehealthInfo.telehealth_kind !== 'in_person' ? (
                  <Videocam />
                ) : undefined
              }
              label={telehealthChip.label}
              color={telehealthChip.color}
              size="small"
            />
            <Badge color={getStatusBadgeColor(appointment.status)} className="text-sm">
              {appointment.status}
            </Badge>
            {providerId && (
              <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}/appointments`}>
                <Button variant="outlined" size="small">
                  Back to Appointments
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Schedule */}
        <Card>
          <CardHeader
            title={
              <CardTitle className="flex items-center">
                <AccessTime className="mr-2 h-5 w-5" />
                Schedule
              </CardTitle>
            }
          />
          <CardContent>
            <div className="space-y-2">
              <DetailRow label="Starts" value={<DateValue value={appointment.start_time} />} />
              <DetailRow
                label="Ends"
                value={endTime ? formatDateTime(endTime) : <span className="text-gray-400">—</span>}
              />
              <DetailRow
                label="Duration"
                value={
                  appointment.duration ? (
                    `${appointment.duration} min`
                  ) : (
                    <span className="text-gray-400">—</span>
                  )
                }
              />
              <DetailRow
                label="Status"
                value={
                  <Badge color={getStatusBadgeColor(appointment.status)}>
                    {appointment.status}
                  </Badge>
                }
              />
              <DetailRow
                label="Series"
                value={
                  appointment.series_id ? (
                    <IdChip label="series" value={appointment.series_id} />
                  ) : (
                    <span className="text-gray-400">One-off</span>
                  )
                }
              />
              <DetailRow label="Created" value={<DateValue value={appointment.created_at} />} />
              <DetailRow label="Updated" value={<DateValue value={appointment.updated_at} />} />
            </div>
          </CardContent>
        </Card>

        {/* Session */}
        <Card>
          <CardHeader
            title={
              <CardTitle className="flex items-center">
                <Videocam className="mr-2 h-5 w-5" />
                Session
              </CardTitle>
            }
          />
          <CardContent>
            <div className="space-y-2">
              <DetailRow
                label="Type"
                value={appointment.encounter_type || <span className="text-gray-400">—</span>}
              />
              <DetailRow
                label="Modality"
                value={
                  appointment.modality ? (
                    MODALITY_LABELS[appointment.modality] || appointment.modality
                  ) : (
                    <span className="text-gray-400">—</span>
                  )
                }
              />
              <DetailRow
                label="Place of service"
                value={appointment.place_of_service || <span className="text-gray-400">—</span>}
              />
              <DetailRow label="Telehealth" value={telehealthChip.label} />
              {telehealthInfo?.jotmeet_intent != null && (
                <DetailRow
                  label="JotMeet intent"
                  value={telehealthInfo.jotmeet_intent ? 'Yes' : 'No'}
                />
              )}
              <DetailRow
                label="JotMeet room"
                value={
                  telehealthInfo?.jotmeet_room_created ? (
                    `Created${
                      telehealthInfo.jotmeet_room_status
                        ? ` (${telehealthInfo.jotmeet_room_status})`
                        : ''
                    }`
                  ) : (
                    <span className="text-gray-400">None</span>
                  )
                }
              />
              <DetailRow
                label="Patient"
                value={
                  appointment.patient_id ? (
                    <ExplorableId
                      label="patient"
                      value={appointment.patient_id}
                      resourceType="patient"
                      organizationId={appointment.organization_id}
                    />
                  ) : (
                    <span className="text-gray-400">—</span>
                  )
                }
              />
              <DetailRow
                label="Template"
                value={
                  appointment.template_id ? (
                    <IdChip label="template" value={appointment.template_id} />
                  ) : (
                    <span className="text-gray-400">—</span>
                  )
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Related Encounter */}
        {encounter ? (
          <Card>
            <CardHeader
              title={
                <CardTitle className="flex items-center">
                  <Event className="mr-2 h-5 w-5" />
                  Related Encounter
                </CardTitle>
              }
            />
            <CardContent>
              <div className="space-y-2">
                <DetailRow
                  label="Encounter"
                  value={<IdChip label="encounter" value={encounter.encounter_id} />}
                />
                <DetailRow
                  label="Status"
                  value={
                    <Badge color={getStatusBadgeColor(encounter.status)}>{encounter.status}</Badge>
                  }
                />
                <DetailRow label="Created" value={<DateValue value={encounter.created_at} />} />
                <DetailRow label="Input documents" value={encounter.input_documents ?? 0} />
              </div>
              <div className="mt-4">
                <Link to={encounterUrl}>
                  <Button variant="outlined" size="small" className="w-full">
                    View Encounter
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title={
                <CardTitle className="flex items-center">
                  <Event className="mr-2 h-5 w-5" />
                  Related Encounter
                </CardTitle>
              }
            />
            <CardContent>
              <p className="text-sm text-gray-500">
                No encounter associated with this appointment.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
